import { useRef, useEffect, useCallback, useState } from 'react'
import { Button, theme } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'

interface Props {
  value?: string | null
  onChange?: (base64: string | null) => void
  label?: string
  disabled?: boolean
}

export function SignatureCanvas({ value, onChange, label = 'Firma', disabled = false }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const lastPos = useRef<{ x: number; y: number } | null>(null)
  const [isEmpty, setIsEmpty] = useState(true)
  const { token } = theme.useToken()

  const getPos = (e: TouchEvent | MouseEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    if ('touches' in e) {
      const t = e.touches[0]
      return { x: (t.clientX - rect.left) * scaleX, y: (t.clientY - rect.top) * scaleY }
    }
    return { x: ((e as MouseEvent).clientX - rect.left) * scaleX, y: ((e as MouseEvent).clientY - rect.top) * scaleY }
  }

  const startDraw = useCallback((e: TouchEvent | MouseEvent) => {
    if (disabled) return
    e.preventDefault()
    drawing.current = true
    const canvas = canvasRef.current!
    lastPos.current = getPos(e, canvas)
  }, [disabled])

  const draw = useCallback((e: TouchEvent | MouseEvent) => {
    if (!drawing.current || disabled) return
    e.preventDefault()
    const canvas = canvasRef.current!
    const ctx = canvas.getContext('2d')!
    const pos = getPos(e, canvas)
    if (lastPos.current) {
      ctx.beginPath()
      ctx.moveTo(lastPos.current.x, lastPos.current.y)
      ctx.lineTo(pos.x, pos.y)
      ctx.strokeStyle = token.colorText
      ctx.lineWidth = 2.5
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.stroke()
    }
    lastPos.current = pos
    setIsEmpty(false)
  }, [disabled, token.colorText])

  const endDraw = useCallback(() => {
    if (!drawing.current) return
    drawing.current = false
    lastPos.current = null
    const canvas = canvasRef.current!
    onChange?.(canvas.toDataURL('image/png'))
  }, [onChange])

  const clear = useCallback(() => {
    const canvas = canvasRef.current!
    const ctx = canvas.getContext('2d')!
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setIsEmpty(true)
    onChange?.(null)
  }, [onChange])

  // Se arriva un value esterno (es. prefill da DB) lo disegniamo
  useEffect(() => {
    if (!value) return
    const canvas = canvasRef.current!
    const img = new Image()
    img.onload = () => {
      const ctx = canvas.getContext('2d')!
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0)
      setIsEmpty(false)
    }
    img.src = value
  }, []) // solo al mount

  useEffect(() => {
    const canvas = canvasRef.current!
    canvas.addEventListener('touchstart', startDraw, { passive: false })
    canvas.addEventListener('touchmove', draw, { passive: false })
    canvas.addEventListener('touchend', endDraw)
    canvas.addEventListener('mousedown', startDraw as EventListener)
    canvas.addEventListener('mousemove', draw as EventListener)
    canvas.addEventListener('mouseup', endDraw)
    canvas.addEventListener('mouseleave', endDraw)
    return () => {
      canvas.removeEventListener('touchstart', startDraw as EventListener)
      canvas.removeEventListener('touchmove', draw as EventListener)
      canvas.removeEventListener('touchend', endDraw)
      canvas.removeEventListener('mousedown', startDraw as EventListener)
      canvas.removeEventListener('mousemove', draw as EventListener)
      canvas.removeEventListener('mouseup', endDraw)
      canvas.removeEventListener('mouseleave', endDraw)
    }
  }, [startDraw, draw, endDraw])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <span style={{ fontSize: 13, color: token.colorTextSecondary, fontWeight: 500 }}>{label}</span>
        {!disabled && (
          <Button
            size="small"
            icon={<DeleteOutlined />}
            onClick={clear}
            disabled={isEmpty}
            style={{ minHeight: 32, minWidth: 32 }}
          >
            Cancella
          </Button>
        )}
      </div>
      <div style={{
        border: `1.5px solid ${isEmpty && !disabled ? token.colorPrimary : token.colorBorder}`,
        borderRadius: token.borderRadius,
        background: token.colorBgContainer,
        overflow: 'hidden',
        touchAction: 'none',
        cursor: disabled ? 'default' : 'crosshair',
        boxShadow: isEmpty && !disabled ? `0 0 0 2px ${token.colorPrimaryBg}` : undefined,
      }}>
        <canvas
          ref={canvasRef}
          width={600}
          height={180}
          style={{ width: '100%', height: 180, display: 'block' }}
        />
      </div>
      {isEmpty && !disabled && (
        <span style={{ fontSize: 12, color: token.colorTextPlaceholder }}>
          Firma qui con il dito o con il mouse
        </span>
      )}
    </div>
  )
}
